import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { EMPTY, Subject, catchError, switchMap, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import {
  KAPCHARGE_RESPONSE_CODE_LABELS,
  KapchargeBatch,
  KapchargeChildSearchParams,
  KapchargeDueDateBreakdown,
  KapchargeLocalTransaction,
  KapchargeLocalTransactionResponse,
  KapchargeTransactionDetail,
  KapchargeTransactionEvent,
  PaymentProcessingService,
  getResponseCodeLabel,
} from '@lmc-app/core/services/payment-processing.service';
import { FormatUtil } from '@lmc-app/shared/utils/format.util';
import { fundingTypeLabel } from '@lmc-app/loan-servicing/payment-processing/funding-type.constant';
import { CUSTOM_DATE_FORMAT } from '@lmc-app/shared/constants/common.constant';
import { LmcDialogComponent } from '@lmc-app/shared/components/dialog/lmc-dialog.component';
import { TransactionEditDialogComponent } from './transaction-edit-dialog.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  StatusBreakdownComponent,
  StatusBreakdownItem,
} from '@lmc-app/shared/components/status-breakdown/status-breakdown.component';
import { kapchargeStatusItems } from '@lmc-app/loan-servicing/payment-processing/kapcharge-status-breakdown.constant';
import {
  KAPCHARGE_CANCELLABLE_STATUSES,
  KAPCHARGE_EDITABLE_STATUSES,
  KAPCHARGE_FAILURE_STATUSES,
  KAPCHARGE_REFUNDABLE_STATUSES,
  KAPCHARGE_STATUS_FILTER_OPTIONS,
  KapchargeTransactionFlow,
  KapchargeTransactionStatus,
} from '@lmc-app/shared/enum/kapcharge-transaction-status.enum';

type DetailCacheValue = KapchargeTransactionDetail | 'loading' | 'error';
type SelectMode = 'refund' | 'export' | 'retry' | 'cancel' | null;

/**
 * Batch Details page — route `/loan-servicing/payment-processing/:batchId`.
 *
 * Was a 95vw x 90vh MatDialog. It is a page now because it behaves like one: a filter form,
 * a paginated table with expandable rows, four bulk-action modes — and, above all, because
 * an operator investigating a batch needs a URL to refresh, bookmark and paste into a
 * ticket, which a dialog cannot give them. The way back up is the back button in this page's
 * own header.
 *
 * It loads the batch its URL names rather than being handed one, which is what makes a cold
 * load work. Nothing here refreshes the list on the way out: leaving is a navigation, so
 * the list is re-created and reloads by itself.
 */
@Component({
  selector: 'app-transaction-batch',
  templateUrl: './transaction-batch.component.html',
  styleUrls: ['./transaction-batch.component.scss'],
  standalone: true,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
      state('expanded', style({ height: '*', visibility: 'visible' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatTableModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatCheckboxModule,
    StatusBreakdownComponent,
  ],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMAT },
  ],
})
export class TransactionBatchComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  readonly FormatUtil = FormatUtil;
  readonly getResponseCodeLabel = getResponseCodeLabel;
  readonly fundingTypeLabel = fundingTypeLabel;

  /**
   * Route to the batch list — this page's own parent. Used by the header's back button and by
   * the not-found state.
   *
   * A routerLink rather than history.back(): navigating to the parent path is what deactivates
   * this page, and it behaves the same whether the operator arrived from the list or opened
   * the URL cold (where there is no history entry to go back to).
   */
  readonly listRoute = '/loan-servicing/payment-processing';

  /** Status filter options — Error stands for the grouped Error + TxnError bucket. */
  readonly transactionStatuses = KAPCHARGE_STATUS_FILTER_OPTIONS;

  /** The batch this page is about; null until its request answers. */
  batch: KapchargeBatch | null = null;
  /** True while the batch itself is being fetched (not its transactions). */
  isBatchLoading = true;
  /** True once the batch request answered 404 — the URL names a batch that is not there. */
  batchNotFound = false;

  /**
   * One status breakdown per due date the batch covers, in the order the backend returned
   * them (ascending). Set once the batch resolves, and not recomputed after a bulk action —
   * the header reads as-of-load, matching the batch row it was opened from.
   */
  dueDateBreakdowns: { entry: KapchargeDueDateBreakdown; items: StatusBreakdownItem[] }[] = [];

  /** The batch's creation date — the only date kept for a batch. */
  get batchDate(): string {
    return this.batch?.createdAt ? FormatUtil.formatDate(this.batch.createdAt, 'YYYY-MM-DD') : '—';
  }

  /** Badge modifier for the batch's file type ('EFT' when the batch has none). */
  fileTypeBadgeClass(fileType: string | null): string {
    return `badge-${(fileType ?? 'EFT').toLowerCase()}`;
  }

  readonly returnCodeOptions: { value: string; label: string }[] = Object.entries(
    KAPCHARGE_RESPONSE_CODE_LABELS
  ).map(([value, label]) => ({ value, label }));

  filterForm = new FormGroup({
    dueDateFrom: new FormControl<Date | null>(null),
    // Disabled until a From date exists: a range needs its start before its end means
    // anything. syncDueDateTo() owns the enable/disable and the clearing that goes with it.
    dueDateTo: new FormControl<Date | null>({ value: null, disabled: true }),
    transactionStatus: new FormControl<string | null>(null),
    returnCode: new FormControl<string | null>(null),
    loanNumber: new FormControl<string>(''),
    name: new FormControl<string>(''),
  });

  readonly displayedColumns = ['select', 'status', 'context', 'flow', 'loanNumber', 'customerName', 'bankDetails', 'dueDate', 'amount', 'actions'];

  /** Exposed for the template's status-driven Context column. */
  readonly KapchargeTransactionStatus = KapchargeTransactionStatus;

  records: KapchargeLocalTransaction[] = [];
  expandedRow: KapchargeLocalTransaction | null = null;
  isLoading = false;
  totalRows = 0;
  pageSize = 50;
  currentPage = 0;

  detailCache = new Map<number, DetailCacheValue>();

  /** Which bulk action the checkbox column + floating bar are currently driving. */
  selectMode: SelectMode = null;
  /** Ids currently selected for the active selectMode action. */
  readonly selected = new Set<number>();
  /** True while the refund POST is in flight — keeps the table visible, unlike isLoading. */
  refunding = false;
  /** True while the EFT export request is in flight. */
  exporting = false;
  /** True while the retry POST is in flight. */
  retrying = false;
  /** True while the cancel POST is in flight. */
  cancelling = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly paymentProcessingService: PaymentProcessingService,
    private readonly snackBar: MatSnackBar,
    private readonly dialog: MatDialog,
    private readonly dateAdapter: DateAdapter<unknown>
  ) {}

  ngOnInit(): void {
    // Due Date To follows Due Date From: enabled only once From has a value, and never left
    // holding a date before it. Reset() clears From, so this keeps To in step there too.
    this.filterForm.controls.dueDateFrom.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((from) => this.syncDueDateTo(from));

    // Driven off the param rather than read once, so the page also follows a batchId that
    // changes under a reused component instance. The failure is caught inside the inner
    // observable, not in the subscriber, so an error on one batchId does not tear down the
    // param subscription and leave the page deaf to the next one.
    this.route.paramMap
      .pipe(
        switchMap((params) => {
          this.resetForBatchChange();
          return this.paymentProcessingService.getBatch(params.get('batchId') ?? '').pipe(
            catchError((err) => {
              this.onBatchLoadFailed(err);
              return EMPTY;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((batch) => {
        this.batch = batch;
        this.isBatchLoading = false;
        this.dueDateBreakdowns = (batch.dueDateBreakdown ?? []).map((entry) => ({
          entry,
          items: kapchargeStatusItems(entry),
        }));
        this.loadChildren();
      });
  }

  /**
   * A 404 is the URL naming a batch that is not there — its own page state, not a failure
   * to report. Anything else is a real error and gets a snackbar.
   */
  private onBatchLoadFailed(err: HttpErrorResponse): void {
    this.isBatchLoading = false;
    this.batchNotFound = err?.status === 404;
    if (!this.batchNotFound) {
      this.snackBar.open(
        err?.error?.message || 'Failed to load this batch.',
        'Close',
        { duration: 5000, panelClass: 'snackbar-error' }
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Clears everything tied to the previous batch before a new one is fetched. */
  private resetForBatchChange(): void {
    this.batch = null;
    this.isBatchLoading = true;
    this.batchNotFound = false;
    this.dueDateBreakdowns = [];
    this.records = [];
    this.totalRows = 0;
    this.currentPage = 0;
    this.expandedRow = null;
    this.detailCache.clear();
    this.exitSelectMode();
  }

  /**
   * Lower bound for the Due Date To picker: the From date, which To must not precede. Null
   * while To is disabled anyway, so the picker simply has no bound to apply.
   */
  get dueDateMin(): Date | null {
    return this.filterForm.controls.dueDateFrom.value;
  }

  /**
   * Keeps Due Date To consistent with the From it depends on. Called on every From change.
   *
   * No From means no range: To is cleared and disabled. A From that is later than the To
   * already picked would make the range empty, so that To is cleared as well — the picker's
   * `[min]` stops the operator choosing such a date, but not a From moved past a date that
   * was already there. Events are suppressed: this runs inside a From change already, and
   * only the search that follows needs to see the result.
   */
  private syncDueDateTo(from: Date | null | undefined): void {
    const to = this.filterForm.controls.dueDateTo;

    if (!from) {
      to.setValue(null, { emitEvent: false });
      to.disable({ emitEvent: false });
      return;
    }

    to.enable({ emitEvent: false });
    if (to.value && dayjs(to.value).isBefore(dayjs(from), 'day')) {
      to.setValue(null, { emitEvent: false });
    }
  }

  /**
   * The due date the table is currently narrowed to, or null when it is not narrowed to a
   * single day. Derived from the form rather than tracked separately, so it also lights up
   * when the operator picks the same date in both pickers, and Reset clears it for free.
   */
  get activeDueDate(): string | null {
    const { dueDateFrom, dueDateTo } = this.filterForm.value;
    if (!dueDateFrom || !dueDateTo) return null;
    const from = dayjs(dueDateFrom).format('YYYY-MM-DD');
    return from === dayjs(dueDateTo).format('YYYY-MM-DD') ? from : null;
  }

  /**
   * Narrows the table to one due date, or clears the range when that date is already the
   * active one — the breakdown blocks act as a toggle.
   *
   * The value goes through the DateAdapter because this page runs on MomentDateAdapter:
   * the controls are declared `Date` but hold Moments, and a plain `Date` in them makes the
   * datepicker input throw on format. Reads elsewhere use dayjs, which accepts both. Each
   * control gets its own instance — a Moment is mutable, so they must not share one.
   *
   * From is set before To, not together: setting From runs syncDueDateTo(), which is what
   * enables To (or clears and disables it) — so To must be written after that has happened.
   */
  selectDueDate(dueDate: string): void {
    const clear = this.activeDueDate === dueDate;
    const value = () => (clear ? null : this.dateAdapter.deserialize(dueDate)) as Date | null;

    this.filterForm.controls.dueDateFrom.setValue(value());
    this.filterForm.controls.dueDateTo.setValue(value());
    this.onSearch();
  }

  onSearch(): void {
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadChildren();
  }

  onReset(): void {
    this.filterForm.reset();
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadChildren();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadChildren();
  }

  isExpandable(row: KapchargeLocalTransaction): boolean {
    return row.kapchargeTransactionId != null;
  }

  /** Anything not yet sent to KapCharge can still be corrected in place. */
  isEditable(row: KapchargeLocalTransaction): boolean {
    return KAPCHARGE_EDITABLE_STATUSES.includes(row.status as KapchargeTransactionStatus);
  }

  /**
   * Opens the transaction editor — still a dialog: it is one row's short form, and it must
   * not cost the operator the table they are working through. It answers with the saved
   * row, which goes through the same path as the bulk actions — patched in place, or
   * reloaded when the current filter means an Invalid row that just re-queued no longer
   * belongs on this page.
   */
  onEditTransaction(row: KapchargeLocalTransaction, event: MouseEvent): void {
    event.stopPropagation();
    const ref = this.dialog.open(TransactionEditDialogComponent, {
      width: '480px',
      data: { transaction: row },
    });
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        this.snackBar.open('Transaction updated.', 'Close', {
          duration: 5000,
          panelClass: 'snackbar-success',
        });
        this.applyRowUpdates([result]);
      }
    });
  }

  /** Only failed transactions (Error or a KapCharge decline) can be retried. */
  isRetryable(row: KapchargeLocalTransaction): boolean {
    return KAPCHARGE_FAILURE_STATUSES.includes(row.status as KapchargeTransactionStatus);
  }

  /**
   * Retries the selected failed transactions. Resets them to Queued on the backend so the
   * scheduled task re-sends them; the affected rows are patched from the response.
   */
  onRetrySelected(): void {
    if (this.selected.size === 0 || this.retrying) return;
    const transactionIds = Array.from(this.selected);
    this.retrying = true;

    this.paymentProcessingService
      .retryTransactions(transactionIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.retrying = false;
          this.exitSelectMode();
          this.snackBar.open(`${result.count} transaction(s) queued for retry.`, 'Close', {
            duration: 5000,
            panelClass: 'snackbar-success',
          });
          this.applyRowUpdates(result.transactions);
        },
        error: (err) => {
          this.retrying = false;
          this.snackBar.open(
            err?.error?.message || 'Failed to retry transactions.',
            'Close',
            { duration: 5000, panelClass: 'snackbar-error' }
          );
        },
      });
  }

  /**
   * Only Processing or Accepted Collect-flow transactions can be refunded: a Pay-flow
   * transaction (already a refund) cannot itself be refunded, and a transaction that
   * already has a refund cannot be refunded again.
   */
  isRefundable(row: KapchargeLocalTransaction): boolean {
    return KAPCHARGE_REFUNDABLE_STATUSES.includes(row.status as KapchargeTransactionStatus)
      && row.transactionFlow !== KapchargeTransactionFlow.Pay
      && !row.alreadyRefunded;
  }

  /**
   * No status restriction on EFT export today — any transaction may be selected.
   * Kept as its own function (mirrors isExportEligible in the backend) so a future
   * restriction only needs to change this one place.
   */
  isExportable(_row: KapchargeLocalTransaction): boolean {
    return true;
  }

  /** Only transactions not yet sent to KapCharge (Queued / Invalid / Error) can be cancelled. */
  isCancellable(row: KapchargeLocalTransaction): boolean {
    return KAPCHARGE_CANCELLABLE_STATUSES.includes(row.status as KapchargeTransactionStatus);
  }

  /** A row's checkbox is only clickable while a select mode is active, and only if it's eligible for that mode's action. */
  canSelectRow(row: KapchargeLocalTransaction): boolean {
    if (this.selectMode === 'refund') return this.isRefundable(row);
    if (this.selectMode === 'export') return this.isExportable(row);
    if (this.selectMode === 'retry') return this.isRetryable(row);
    if (this.selectMode === 'cancel') return this.isCancellable(row);
    return false;
  }

  /** Tooltip explaining why a row's checkbox is disabled; empty while it's selectable. */
  getSelectDisabledReason(row: KapchargeLocalTransaction): string {
    if (this.selectMode === null || this.canSelectRow(row)) return '';
    if (this.selectMode === 'refund') {
      if (row.transactionFlow === KapchargeTransactionFlow.Pay) return 'This is already a refund (Pay) transaction and cannot be refunded again.';
      if (row.alreadyRefunded) return 'This transaction has already been refunded.';
      return 'Only Processing or Accepted transactions can be refunded.';
    }
    if (this.selectMode === 'retry') {
      return 'Only failed transactions can be retried.';
    }
    if (this.selectMode === 'cancel') {
      return 'Only transactions not yet sent to KapCharge can be cancelled.';
    }
    return '';
  }

  /** Rows eligible for the currently active select mode; empty when no mode is active. */
  get selectableRows(): KapchargeLocalTransaction[] {
    return this.records.filter((r) => this.canSelectRow(r));
  }

  enterSelectMode(mode: 'refund' | 'export' | 'retry' | 'cancel'): void {
    this.selectMode = mode;
  }

  getSelectAllTooltip(): string {
    if (this.selectMode === 'refund') return 'Select all refundable (Processing or Accepted)';
    if (this.selectMode === 'export') return 'Select all for export';
    if (this.selectMode === 'retry') return 'Select all retryable (failed transactions)';
    if (this.selectMode === 'cancel') return 'Select all cancellable (not yet sent to KapCharge)';
    return "Click 'Retry', 'Refund', 'Export EFT' or 'Cancel' to select rows";
  }

  exitSelectMode(): void {
    this.selectMode = null;
    this.selected.clear();
  }

  toggleRow(row: KapchargeLocalTransaction): void {
    if (!this.canSelectRow(row)) return;
    this.selected.has(row.id) ? this.selected.delete(row.id) : this.selected.add(row.id);
  }

  isAllSelected(): boolean {
    const rows = this.selectableRows;
    return rows.length > 0 && rows.every((r) => this.selected.has(r.id));
  }

  isSomeSelected(): boolean {
    return this.selected.size > 0 && !this.isAllSelected();
  }

  toggleAllSelectable(): void {
    if (this.selectMode === null) return;
    this.isAllSelected()
      ? this.selected.clear()
      : this.selectableRows.forEach((r) => this.selected.add(r.id));
  }

  /**
   * Confirms with the user, then creates a refund batch for the selected transactions.
   * The backend creates a new "Pay"-flow batch reversing them; the source rows are
   * left untouched.
   */
  onRefundSelected(): void {
    if (this.selected.size === 0 || this.refunding) return;
    const count = this.selected.size;

    const dialogRef = this.dialog.open(LmcDialogComponent, {
      data: {
        title: 'Refund Selected Transactions',
        content: `Are you sure you want to refund ${count} selected transaction(s)? This creates a new batch that reverses them; it cannot be undone.`,
        positiveButtonText: 'Refund',
        negativeButtonText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed === true) {
        this.submitRefund();
      }
    });
  }

  private submitRefund(): void {
    const transactionIds = Array.from(this.selected);
    this.refunding = true;

    this.paymentProcessingService
      .refundTransactions(transactionIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.refunding = false;
          this.exitSelectMode();
          this.snackBar.open(`Refund batch created: ${result.count} transaction(s) queued.`, 'Close', {
            duration: 6000,
            panelClass: 'snackbar-success',
          });

          // A refund leaves the source rows untouched in the database — it only creates
          // Pay-flow rows in a new batch, which this table does not show. The single
          // visible consequence is the derived `alreadyRefunded` flag, and the call is
          // all-or-nothing (any ineligible row 409s the whole request), so every id sent
          // is now refunded. Nothing here can affect a filter, so no reload either. The
          // new batch shows up in the list on its own, since going back re-loads it.
          const refunded = new Set(transactionIds);
          this.records.forEach((row) => {
            if (refunded.has(row.id)) row.alreadyRefunded = true;
          });
        },
        error: (err) => {
          this.refunding = false;
          this.snackBar.open(
            err?.error?.message || 'Failed to create refund batch.',
            'Close',
            { duration: 5000, panelClass: 'snackbar-error' }
          );
        },
      });
  }

  /**
   * Confirms with the user, then cancels the selected transactions. The backend flips each
   * to Cancelled so the scheduled submit task skips them; the list is reloaded to reflect it.
   */
  onCancelSelected(): void {
    if (this.selected.size === 0 || this.cancelling) return;
    const count = this.selected.size;

    const dialogRef = this.dialog.open(LmcDialogComponent, {
      data: {
        title: 'Cancel Selected Transactions',
        content: `Are you sure you want to cancel ${count} selected transaction(s)? They will be marked as Cancelled and will not be sent to KapCharge.`,
        positiveButtonText: 'Yes',
        negativeButtonText: 'No',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed === true) {
        this.submitCancel();
      }
    });
  }

  private submitCancel(): void {
    const transactionIds = Array.from(this.selected);
    this.cancelling = true;

    this.paymentProcessingService
      .cancelTransactions(transactionIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.cancelling = false;
          this.exitSelectMode();
          this.snackBar.open(`${result.count} transaction(s) cancelled.`, 'Close', {
            duration: 5000,
            panelClass: 'snackbar-success',
          });
          this.applyRowUpdates(result.transactions);
        },
        error: (err) => {
          this.cancelling = false;
          this.snackBar.open(
            err?.error?.message || 'Failed to cancel transactions.',
            'Close',
            { duration: 5000, panelClass: 'snackbar-error' }
          );
        },
      });
  }

  /**
   * Builds and downloads a CPA 005 EFT file from the selected transactions.
   * Non-destructive (no confirm dialog needed) — this only reads data.
   */
  onExportSelected(): void {
    if (this.selected.size === 0 || this.exporting || !this.batch) return;
    const batch = this.batch;
    const transactionIds = Array.from(this.selected);
    this.exporting = true;

    this.paymentProcessingService
      .exportEftFile(transactionIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          this.exporting = false;
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${batch.originatorId}_${batch.fileType}_EXPORT.txt`;
          a.click();
          URL.revokeObjectURL(url);
          this.exitSelectMode();
        },
        error: (err) => {
          this.exporting = false;
          this.snackBar.open(
            err?.error?.message || 'Failed to export EFT file.',
            'Close',
            { duration: 5000, panelClass: 'snackbar-error' }
          );
        },
      });
  }

  onRowClick(row: KapchargeLocalTransaction): void {
    if (!this.isExpandable(row)) return;
    const isExpanding = this.expandedRow !== row;
    this.expandedRow = isExpanding ? row : null;
    if (isExpanding) {
      const id = row.kapchargeTransactionId!;
      if (!this.detailCache.has(id)) {
        this.detailCache.set(id, 'loading');
        this.paymentProcessingService
          .getTransaction(id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (detail) => this.detailCache.set(id, detail),
            error: () => this.detailCache.set(id, 'error'),
          });
      }
    }
  }

  getDetail(row: KapchargeLocalTransaction): DetailCacheValue | undefined {
    if (!row.kapchargeTransactionId) return undefined;
    return this.detailCache.get(row.kapchargeTransactionId);
  }

  getSortedEvents(row: KapchargeLocalTransaction): KapchargeTransactionEvent[] {
    const detail = this.getDetail(row);
    if (!detail || detail === 'loading' || detail === 'error') return [];
    return [...(detail as KapchargeTransactionDetail).transactionEvents].sort(
      (a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime()
    );
  }

  readonly getStatusColor = KapchargeTransactionStatus.getColor;

  getFlowClass(flow: string | null): string {
    const lower = flow?.toLowerCase();
    return lower === 'debit' || lower === 'collect' ? 'flow-debit' : 'flow-credit';
  }

  getEventMarkerClass(eventStatus?: string): string {
    if (!eventStatus) return 'marker-info';
    switch (eventStatus.toLowerCase()) {
      case 'successful': return 'marker-success';
      case 'failed':
      case 'error': return 'marker-error';
      default: return 'marker-default';
    }
  }

  getEventIcon(eventStatus?: string): string {
    if (!eventStatus) return 'info_outline';
    switch (eventStatus.toLowerCase()) {
      case 'successful': return 'check_circle';
      case 'failed':
      case 'error': return 'cancel';
      default: return 'radio_button_unchecked';
    }
  }

  /**
   * True when a filter is active whose answer a status change could alter. Cancel and retry
   * both rewrite status and returnCode, so under either of those filters the affected rows
   * may no longer belong on this page at all — which also shifts rowCount and what the next
   * page holds. Only the server can say, so those cases reload.
   */
  private hasStatusSensitiveFilter(): boolean {
    const f = this.filterForm.value;
    return !!(f.transactionStatus || f.returnCode);
  }

  /**
   * Reflects a bulk status action without a round trip, reloading only when the current
   * filter makes that unsafe. Shared by cancel and retry, which both answer with the
   * current state of every row they were asked about.
   */
  private applyRowUpdates(updated: KapchargeLocalTransaction[]): void {
    if (this.hasStatusSensitiveFilter()) {
      this.loadChildren();
      return;
    }
    this.patchRows(updated);
  }

  /**
   * Applies server-returned rows onto the matching rows already in the table. Assigning
   * into the existing object keeps the reference the table renders, and leaves keys the
   * response does not carry — the list endpoint's derived `customerName` / `alreadyRefunded`
   * — untouched.
   */
  private patchRows(updated: KapchargeLocalTransaction[]): void {
    if (!updated?.length) return;
    const byId = new Map(updated.map((r) => [r.id, r]));

    this.records.forEach((row) => {
      const fresh = byId.get(row.id);
      if (!fresh) return;
      // Retry detaches the row from its KapCharge transaction, so anything cached under the
      // old id describes a transaction this row no longer points at.
      if (row.kapchargeTransactionId != null && fresh.kapchargeTransactionId !== row.kapchargeTransactionId) {
        this.detailCache.delete(row.kapchargeTransactionId);
      }
      Object.assign(row, fresh);
    });

    // A row that just lost its KapCharge id has no detail left to show.
    if (this.expandedRow && !this.isExpandable(this.expandedRow)) {
      this.expandedRow = null;
    }
  }

  private buildChildParams(page: number, pageSize: number): KapchargeChildSearchParams {
    const form = this.filterForm.value;
    const params: KapchargeChildSearchParams = { page, pageSize };

    if (form.dueDateFrom) params.dueDateFrom = dayjs(form.dueDateFrom).startOf('day').toISOString();
    if (form.dueDateTo) params.dueDateTo = dayjs(form.dueDateTo).endOf('day').toISOString();
    if (form.transactionStatus) params.transactionStatus = form.transactionStatus;
    if (form.returnCode) params.returnCode = form.returnCode;
    if (form.loanNumber?.trim()) params.loanNumber = form.loanNumber.trim();
    if (form.name?.trim()) params.name = form.name.trim();

    return params;
  }

  private loadChildren(): void {
    if (!this.batch) return;
    this.isLoading = true;
    this.detailCache.clear();

    this.paymentProcessingService
      .getTransactionsByBatchId(
        this.batch.batchId,
        this.buildChildParams(this.currentPage + 1, this.pageSize)
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: KapchargeLocalTransactionResponse) => {
          this.records = response.results ?? [];
          this.totalRows = response.rowCount ?? 0;
          this.isLoading = false;
        },
        error: () => {
          this.records = [];
          this.totalRows = 0;
          this.isLoading = false;
        },
      });
  }
}
